import React, { createRef, useEffect, useState } from 'react';

interface ISettings {
  itemHeight: number;
  amount: number;
  tolerance: number;
  minIndex: number;
  maxIndex: number;
  startIndex: number;
  hasFilter: boolean;
}

const calcScrollState = (settings: ISettings) => {
  const { itemHeight, amount, tolerance, minIndex, maxIndex, startIndex, hasFilter } = settings;
  const data: any[] = [];
  const viewportHeight = amount * itemHeight;
  const totalHeight = (maxIndex - minIndex + 1) * itemHeight;
  const toleranceHeight = tolerance * itemHeight;
  const bufferHeight = viewportHeight + 2 * toleranceHeight;
  const bufferedItems = amount + 2 * tolerance;
  const itemsAbove = startIndex - tolerance - minIndex;
  const topPaddingHeight = itemsAbove * itemHeight;
  const bottomPaddingHeight = totalHeight - topPaddingHeight;
  const initialPosition = topPaddingHeight + toleranceHeight;
  const hasFilterValue = hasFilter;
  return {
    settings,
    viewportHeight,
    totalHeight,
    toleranceHeight,
    bufferHeight,
    bufferedItems,
    topPaddingHeight,
    bottomPaddingHeight,
    initialPosition,
    hasFilterValue,
    data,
  };
};

interface IProps {
  settings: ISettings;
  get: (index: number, bufferedItems: number) => any[];
  row: (obj: any) => React.ReactNode;
  height?: number;
}

const VirtualScrollbar: React.FC<IProps> = ({ settings, get, row, height }) => {
  const [scrollState, setScrollState] = useState(calcScrollState(settings));
  const viewportElement = createRef<HTMLDivElement>();

  useEffect(() => {
    viewportElement.current!.scrollTop = 1;
    if (!scrollState.initialPosition) {
      runScroller(0);
    }
  }, []);

  useEffect(() => {
    runScroller(0);
  }, [settings]);

  const runScroller = (scrollTop: number) => {
    const {
      totalHeight,
      toleranceHeight,
      bufferedItems,
      settings: { itemHeight, minIndex, hasFilter },
    } = calcScrollState(settings);
    const index = minIndex + Math.floor((scrollTop - toleranceHeight) / itemHeight);
    const data = get(index, bufferedItems);
    const topPaddingHeight = Math.max((index - minIndex) * itemHeight, 0);
    const bottomPaddingHeight = hasFilter ? Math.max(totalHeight - topPaddingHeight - data.length * itemHeight, 0) : 0;
    setScrollState({
      ...scrollState,
      topPaddingHeight,
      bottomPaddingHeight,
      data,
    });
  };

  return (
    <div
      className="viewport"
      ref={viewportElement}
      onScroll={(e) => {
        runScroller(e.currentTarget.scrollTop);
      }}
      style={{ height: height ?? scrollState.viewportHeight, overflowY: 'auto' }}
    >
      <div style={{ height: scrollState.topPaddingHeight + 20 }} />
      {scrollState.data.map(row)}
      <div style={{ height: scrollState.bottomPaddingHeight }} />
    </div>
  );
};

export default VirtualScrollbar;
